import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';

// Icons
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import conversationUIStyles from '../styles/conversationUIStyles';

// The panel is a fixed 400px column, which is a poor surface for reading a
// clinical summary (ST-20260922-0001). Expanded roughly doubles it; maxWidth on
// the panel keeps that inside the viewport on a narrow screen.
const EXPANDED_WIDTH = 760;
// Per therapist, not per conversation: someone who wants the room wants it every
// time. Kept in this component because nothing else needs to read it.
const EXPANDED_KEY = 'aimhi:summary-panel:expanded';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`summary-tabpanel-${index}`}
      aria-labelledby={`summary-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const SummaryPanel = ({
  open,
  onClose,
  therapistSummary,
  clientSummary,
  reflectionPrompts,
  summariesLoaded,
  summaryLoading,
  summaryError,
  onGenerateSummary,
  isEditingClientSummary,
  editableClientSummaryText,
  onStartEditClientSummary,
  onCancelEditClientSummary,
  onSaveEditedClientSummary,
  onEditableClientSummaryTextChange,
  clientSummaryEditError,
  clientSummaryEditLoading
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [copySuccess, setCopySuccess] = useState(null);

  // A clinical summary is a reading task, and 400px is a narrow column for one
  // (ST-20260922-0001). The choice is remembered per therapist, because someone
  // who wants the room wants it every time, not once per panel open.
  const [expanded, setExpanded] = useState(() => {
    try {
      return localStorage.getItem(EXPANDED_KEY) === 'true';
    } catch {
      // Private windows and blocked site data throw here; a default width is a
      // fine outcome, an unrenderable panel is not.
      return false;
    }
  });

  const toggleExpanded = () => {
    setExpanded(prev => {
      const next = !prev;
      try {
        localStorage.setItem(EXPANDED_KEY, String(next));
      } catch {
        /* the preference simply does not persist; the panel still resizes */
      }
      return next;
    });
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleCopy = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(type);
    setTimeout(() => setCopySuccess(null), 2000);
  };

  const panelStyles = {
    ...conversationUIStyles.slidePanel,
    ...(open && conversationUIStyles.slidePanelOpen),
    // Overridden locally rather than in conversationUIStyles, because
    // ReviewPanel shares that style object and did not ask to be widened.
    // maxWidth keeps it inside the viewport on a phone, where the shared style
    // is already a fixed 400 (the responsive variant never reaches this panel).
    width: expanded ? EXPANDED_WIDTH : conversationUIStyles.slidePanel.width,
    maxWidth: '100vw',
    // The shared style transitions transform only, so without this the panel
    // would jump to its new width instead of moving to it.
    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.2s ease'
  };

  return (
    <>
      {/* Backdrop */}
      <Box
        onClick={onClose}
        sx={{
          ...conversationUIStyles.backdrop,
          ...(open && conversationUIStyles.backdropVisible)
        }}
      />

      {/* Panel — stopPropagation prevents clicks from bubbling to backdrop's onClose */}
      <Box sx={panelStyles} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <Box sx={conversationUIStyles.slidePanelHeader}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Conversation Summary
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={expanded ? 'Narrow the panel' : 'Widen the panel'}>
              <IconButton
                onClick={toggleExpanded}
                size="small"
                aria-label={expanded ? 'Narrow the panel' : 'Widen the panel'}
                data-testid="summary-panel-expand"
              >
                {expanded ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <IconButton onClick={onClose} size="small" aria-label="Close panel">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Therapist View" />
            <Tab label="Client Summary" />
            <Tab label="Reflection Prompts" />
          </Tabs>
        </Box>

        {/* Content */}
        <Box sx={conversationUIStyles.slidePanelContent}>
          {/* Therapist Summary Tab */}
          <TabPanel value={activeTab} index={0}>
            {!summariesLoaded && !summaryLoading && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" sx={{ mb: 2, color: '#64748b' }}>
                  No summary generated yet
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={onGenerateSummary}
                  sx={{
                    background: 'linear-gradient(135deg, #FF8C00 0%, #FFA500 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #FF7700 0%, #FF9500 100%)'
                    }
                  }}
                >
                  Generate Summary
                </Button>
              </Box>
            )}

            {summaryLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {summaryError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {summaryError}
              </Alert>
            )}

            {therapistSummary && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Professional Summary
                  </Typography>
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(therapistSummary, 'therapist')}
                      sx={{ mr: 1 }}
                      aria-label="Copy therapist summary"
                    >
                      {copySuccess === 'therapist' ? <CheckCircleIcon color="success" /> : <ContentCopyIcon />}
                    </IconButton>
                    <IconButton size="small" onClick={onGenerateSummary} aria-label="Regenerate summary">
                      <RefreshIcon />
                    </IconButton>
                  </Box>
                </Box>
                
                <Box sx={{
                  p: 2,
                  backgroundColor: '#f8f9fa',
                  borderRadius: '12px',
                  border: '1px solid rgba(0,0,0,0.06)'
                }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                    {therapistSummary}
                  </Typography>
                </Box>

                {/* Metadata */}
                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label="AI Generated" size="small" color="primary" variant="outlined" />
                  <Chip 
                    label={`Generated ${new Date().toLocaleDateString()}`} 
                    size="small" 
                    variant="outlined" 
                  />
                </Box>
              </Box>
            )}
          </TabPanel>

          {/* Client Summary Tab */}
          <TabPanel value={activeTab} index={1}>
            {!clientSummary && !summaryLoading && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" sx={{ mb: 2, color: '#64748b' }}>
                  No client summary generated yet
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={onGenerateSummary}
                  sx={{
                    background: 'linear-gradient(135deg, #FF8C00 0%, #FFA500 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #FF7700 0%, #FF9500 100%)'
                    }
                  }}
                >
                  Generate Summary
                </Button>
              </Box>
            )}

            {clientSummary && !isEditingClientSummary && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Client-Facing Summary
                  </Typography>
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(clientSummary, 'client')}
                      sx={{ mr: 1 }}
                      aria-label="Copy client summary"
                    >
                      {copySuccess === 'client' ? <CheckCircleIcon color="success" /> : <ContentCopyIcon />}
                    </IconButton>
                    <IconButton size="small" onClick={onStartEditClientSummary} aria-label="Edit client summary">
                      <EditIcon />
                    </IconButton>
                  </Box>
                </Box>
                
                <Box sx={{
                  p: 2,
                  backgroundColor: '#f0f9ff',
                  borderRadius: '12px',
                  border: '1px solid rgba(33,150,243,0.2)'
                }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                    {clientSummary}
                  </Typography>
                </Box>

                <Alert severity="info" sx={{ mt: 2 }}>
                  This summary will be visible to the client in their session history
                </Alert>
              </Box>
            )}

            {isEditingClientSummary && (
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Edit Client Summary
                </Typography>
                
                <TextField
                  fullWidth
                  multiline
                  rows={10}
                  value={editableClientSummaryText}
                  onChange={onEditableClientSummaryTextChange}
                  variant="outlined"
                  sx={{ mb: 2 }}
                />

                {clientSummaryEditError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {clientSummaryEditError}
                  </Alert>
                )}

                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={onCancelEditClientSummary}
                    disabled={clientSummaryEditLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={clientSummaryEditLoading ? <CircularProgress size={16} /> : <SaveIcon />}
                    onClick={onSaveEditedClientSummary}
                    disabled={clientSummaryEditLoading}
                    sx={{
                      background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #388E3C 0%, #2E7D32 100%)'
                      }
                    }}
                  >
                    Save Changes
                  </Button>
                </Box>
              </Box>
            )}
          </TabPanel>

          {/* Reflection Prompts Tab */}
          <TabPanel value={activeTab} index={2}>
            {!reflectionPrompts || reflectionPrompts.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" sx={{ mb: 2, color: '#64748b' }}>
                  No reflection prompts available
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={onGenerateSummary}
                  sx={{
                    background: 'linear-gradient(135deg, #FF8C00 0%, #FFA500 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #FF7700 0%, #FF9500 100%)'
                    }
                  }}
                >
                  Generate Prompts
                </Button>
              </Box>
            ) : (
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Reflection Questions for Client
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {reflectionPrompts.map((prompt, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 2,
                        backgroundColor: '#f3e5f5',
                        borderRadius: '12px',
                        border: '1px solid rgba(156,39,176,0.2)',
                        position: 'relative'
                      }}
                    >
                      <Chip
                        label={`Question ${index + 1}`}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          backgroundColor: '#9C27B0',
                          color: '#ffffff'
                        }}
                      />
                      <Typography variant="body2" sx={{ pr: 8 }}>
                        {prompt}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Alert severity="success" sx={{ mt: 2 }}>
                  These prompts help clients reflect on their progress and insights from the session
                </Alert>
              </Box>
            )}
          </TabPanel>
        </Box>
      </Box>
    </>
  );
};

export default SummaryPanel;